import { useState } from "react";

import { minorUnitsPerUnit } from "../patients/money";
import { Modal } from "../ui/modal";
import { NumberField } from "../ui/number-field";
import { typedMinorUnits } from "../inventory/supplier-text";
import inv from "../inventory/inventory.module.css";
import styles from "../inventory/suppliers.module.css";

/**
 * *Add to stock* at a location that prices in another currency (IA-D9).
 *
 * Supplier prices are in the clinic's home currency (QA4-D11) and a stock item's
 * price is in its location's (QA4-D6), so the number cannot be copied across:
 * this asks what one pack costs here. Opened only when the server says so —
 * a location in the home currency adds the item exactly as before.
 */
export function LocationPriceDialog({
  itemName,
  currency,
  busy,
  onSave,
  onClose,
}: {
  itemName: string;
  /** The location's currency, as the server named it. */
  currency: string;
  busy: boolean;
  onSave: (priceCents: number) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const decimals = Math.round(Math.log10(minorUnitsPerUnit(currency)));
  const [price, setPrice] = useState("");
  const cents = typedMinorUnits(price, currency);
  const valid = cents !== null && cents > 0;

  return (
    <Modal label={`Price ${itemName} here`} onClose={onClose} testId="location-price-dialog">
      <form
        className={inv.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          if (!valid) return;
          void onSave(cents).then((landed) => {
            if (landed) onClose();
          });
        }}
      >
        <h2 className={inv.dialogTitle}>What does one pack of {itemName} cost here?</h2>
        <p className={styles.sub}>
          Supplier prices are in another currency, and this location prices in {currency}.
        </p>
        <NumberField
          label={`Price per pack (${currency})`}
          required
          className={inv.field}
          labelClassName={inv.fieldLabel}
          inputClassName={inv.input}
          value={price}
          onChange={setPrice}
          rules={{ min: 0, integer: decimals === 0, unit: currency }}
          autoFocus
          testId="location-price"
        />
        <div className={inv.dialogActions}>
          <button type="button" className={inv.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={inv.primary}
            disabled={busy || !valid}
            data-testid="location-price-save"
          >
            Add to stock
          </button>
        </div>
      </form>
    </Modal>
  );
}
